import { RangeSetBuilder } from '@codemirror/state';
import { Decoration, DecorationSet } from '@codemirror/view';

import DiffMatchPatch from 'diff-match-patch';
import { useCallback, useMemo } from 'react';

import { DATA_CONVERTERS } from '@/components/Code/Code.utils';
import { DiffChangeType } from '@/types/diff';
import { RunSchema } from '@/types/schema';

import { useLanguageContext } from './useLanguagePreferenceContext/LanguageContext';

export const useRunDiffs = (
  peekedRunData: RunSchema | undefined,
  comparisonRunData: RunSchema | undefined,
  isShowingDiff: boolean,
  isPeekedRunLoading: boolean,
  isComparisonRunLoading: boolean,
  isPeekedRunValidating: boolean,
  isComparisonRunValidating: boolean
) => {
  const isLoading = isPeekedRunLoading || isComparisonRunLoading;
  const isValidating = isPeekedRunValidating || isComparisonRunValidating;
  const [userLang] = useLanguageContext();

  const computeDiffs = useCallback(
    (
      type: 'inputs' | 'outputs',
      baseRunData: RunSchema | undefined,
      compareRunData: RunSchema | undefined
    ): DiffChangeType[] | undefined => {
      const dmp = new DiffMatchPatch();
      if (!compareRunData || !baseRunData) return undefined;

      const converter = DATA_CONVERTERS[userLang];

      const outputString = converter.stringify(compareRunData[type]);
      const baseOutputString = converter.stringify(baseRunData[type]);

      const diffs = dmp.diff_main(baseOutputString, outputString);
      dmp.diff_cleanupSemantic(diffs);
      return diffs;
    },
    [userLang]
  );

  const computeDiffDecorations = useCallback((diffs: DiffChangeType[]) => {
    if (diffs.length === 0)
      return { decoration: Decoration.none, generatedCode: undefined };
    const builder = new RangeSetBuilder();

    let cursor = 0;
    let generatedCode = '';
    diffs.forEach((part, index) => {
      const [change, text] = part;
      const startPos = cursor;
      generatedCode += text;
      const endPos = cursor + text.length;

      // Skip empty text
      if (text.length === 0) {
        console.error('Skipping empty text diff:', { index, change });
        return;
      }

      if (
        startPos >= 0 &&
        endPos <= generatedCode.length &&
        (change === DiffMatchPatch.DIFF_INSERT ||
          change === DiffMatchPatch.DIFF_DELETE)
      ) {
        builder.add(
          startPos,
          endPos,
          Decoration.mark({
            class:
              change === DiffMatchPatch.DIFF_INSERT
                ? 'highlight-added'
                : 'highlight-removed',
          })
        );
      }
      cursor = endPos;
    });
    return { decoration: builder.finish() as DecorationSet, generatedCode };
  }, []);

  const cachedDiffs = useMemo(() => {
    const inputs = computeDiffs('inputs', peekedRunData, comparisonRunData);
    const outputs = computeDiffs('outputs', peekedRunData, comparisonRunData);

    const inputsMap = inputs?.reduce(
      (acc, curr) => {
        const [change] = curr;
        if (change === DiffMatchPatch.DIFF_INSERT) {
          acc.added.push(curr);
        } else if (change === DiffMatchPatch.DIFF_DELETE) {
          acc.removed.push(curr);
        } else {
          acc.added.push(curr);
          acc.removed.push(curr);
        }
        return acc;
      },
      { added: [], removed: [] } as {
        added: [change: number, text: string][];
        removed: [change: number, text: string][];
      }
    );

    const outputsMap = outputs?.reduce(
      (acc, curr) => {
        const [change] = curr;
        if (change === DiffMatchPatch.DIFF_INSERT) {
          acc.added.push(curr);
        } else if (change === DiffMatchPatch.DIFF_DELETE) {
          acc.removed.push(curr);
        } else {
          acc.added.push(curr);
          acc.removed.push(curr);
        }

        return acc;
      },
      { added: [], removed: [] } as {
        added: DiffChangeType[];
        removed: DiffChangeType[];
      }
    );

    return {
      inputs: {
        added: {
          ...computeDiffDecorations(inputsMap?.added ?? []),
        },
        removed: {
          ...computeDiffDecorations(inputsMap?.removed ?? []),
        },
      },
      outputs: {
        added: {
          ...computeDiffDecorations(outputsMap?.added ?? []),
        },
        removed: {
          ...computeDiffDecorations(outputsMap?.removed ?? []),
        },
      },
    };
  }, [computeDiffs, computeDiffDecorations, comparisonRunData, peekedRunData]);

  const getDiffValues = useCallback(
    (
      type: 'inputs' | 'outputs',
      changeType: 'added' | 'removed'
    ):
      | {
          decoration: DecorationSet;
          generatedCode: string | undefined;
        }
      | undefined => {
      // ensure that we only generate diffs on updated data
      if (isLoading || isValidating) return undefined;
      return isShowingDiff ? cachedDiffs[type][changeType] : undefined;
    },
    [isShowingDiff, cachedDiffs, isLoading, isValidating]
  );

  return {
    getDiffValues,
    cachedDiffs,
  };
};
